import type {
  MetricSummary,
  SearchSliceNode,
  SliceMetrics,
  StreamSliceNode,
} from '@/analysis/searchContract'

export const HEX_STREAM_RETAIN_MS = 5 * 60 * 1000

export type StreamRetainState = {
  scopeKey: string | null
  entries: Map<string, { lastSeenMs: number; lastNode: StreamSliceNode }>
}

export function createStreamRetainState(): StreamRetainState {
  return { scopeKey: null, entries: new Map() }
}

function emptyMetric(): MetricSummary {
  return { tier: null, displayValue: null }
}

function nullOutMetrics(src: SliceMetrics | undefined | null): SliceMetrics {
  const keys: Array<keyof SliceMetrics> = [
    'demuxInRate',
    'demuxToDecodeLag',
    'renderRatio',
    'downloadKbps',
    'jitter',
    'packetDelay',
    'dropFrame',
    'pFrameDelay',
    'iFrameDelay',
    'bufferLevel',
    'fps',
    'iFrameCount',
    'pFrameCount',
    'bufferOverRun',
    'bufferUnderRunMs',
    'avgFrameBytes',
    'decodeQueue',
    'mediaLatency',
  ]
  const out = { ...(src ?? {}) } as SliceMetrics
  for (const k of keys) {
    out[k] = emptyMetric()
  }
  return out
}

/** Identity-preserving shell: same stream, no telemetry for this window. */
export function toNoDataStreamShell(node: StreamSliceNode): StreamSliceNode {
  return {
    ...node,
    avgSeverity: null,
    severity: null,
    worstMetric: null,
    worstMetricSeverity: null,
    totalStreams: 0,
    usersTotal: 0,
    tickCount: 0,
    criticalCount: 0,
    warningCount: 0,
    goodCount: 0,
    metrics: nullOutMetrics(node.metrics),
    status: 'idle',
  }
}

function streamKey(n: StreamSliceNode): string {
  return String(n.streamId ?? n.value ?? '').trim()
}

/**
 * When Active only is on, keep streams that appeared in the last `retainMs`
 * even if the latest tick omitted them — but clear metrics so the tile shows
 * no-data instead of stale numbers or disappearing.
 *
 * Mutates `state` (caller should hold it in a ref). Resets when `scopeKey` changes.
 */
export function retainRecentStreamNodes(
  incoming: SearchSliceNode[],
  opts: {
    enabled: boolean
    nowMs: number
    scopeKey: string
    retainMs?: number
    state: StreamRetainState
  },
): SearchSliceNode[] {
  const { enabled, nowMs, scopeKey, state } = opts
  const retainMs = opts.retainMs ?? HEX_STREAM_RETAIN_MS

  if (!enabled) {
    state.scopeKey = null
    state.entries.clear()
    return incoming
  }

  if (state.scopeKey !== scopeKey) {
    state.scopeKey = scopeKey
    state.entries.clear()
  }

  const liveStreams = incoming.filter((n): n is StreamSliceNode => n.kind === 'stream')
  const nonStreams = incoming.filter((n) => n.kind !== 'stream')
  const liveKeys = new Set<string>()

  for (const n of liveStreams) {
    const key = streamKey(n)
    if (!key) continue
    liveKeys.add(key)
    state.entries.set(key, { lastSeenMs: nowMs, lastNode: n })
  }

  const retained: StreamSliceNode[] = []
  for (const [key, entry] of state.entries) {
    if (nowMs - entry.lastSeenMs > retainMs) {
      state.entries.delete(key)
      continue
    }
    if (liveKeys.has(key)) {
      retained.push(entry.lastNode)
    } else {
      retained.push(toNoDataStreamShell(entry.lastNode))
    }
  }

  return [...nonStreams, ...retained]
}
